import { API_URL } from 'config';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { GlassCard } from '../UI/GlassCard';
import { motion, AnimatePresence } from 'framer-motion';

export const PricingTable = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('modules');
  const [prices, setPrices] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/license/prices`, { method: 'GET', credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'ok') setPrices(data.modules || {});
      })
      .catch(() => {});
  }, []);

  const getModulePrice = (moduleId, defaultText) => {
    const price = prices[moduleId]?.price;
    return price !== undefined ? `${price} $` : defaultText;
  };

  const tabs = [
    { id: 'modules', label: t('pricing.tabModules') },
    { id: 'partners', label: t('pricing.tabPartners') }
  ];

  const handleActionClick = (product, method) => {
    navigate('/cabinet');
  };

  const renderContent = () => {
    const animationProps = {
      initial: { opacity: 0, y: 15 },
      whileInView: { opacity: 1, y: 0 },
      viewport: { once: true, amount: 0.1 },
      exit: { opacity: 0, y: -15 },
      transition: { duration: 0.3 }
    };

    switch (activeTab) {
      case 'modules':
        const moduleIds = ['module_1', 'module_2', 'module_3', 'module_4', 'module_5'];
        return (
          <motion.div key="modules" {...animationProps} className="pricing-grid modules-pricing-grid">
            {moduleIds.map((moduleId, index) => {
              const moduleData = t(`modules.${moduleId}`);
              const defaultPrice = (moduleData.features?.[0] || '').match(/([\d.]+)\s*\$/)?.[0] || `${moduleData.features?.[0]?.match(/[\d.]+/)?.[0] || 0} $`;
              const price = getModulePrice(moduleId, defaultPrice);
              const features = (moduleData.features || []).slice(1);
              const isHighlighted = moduleId === 'module_4';

              return (
                <GlassCard key={moduleId} className={`pricing-card ${isHighlighted ? 'highlight-card' : ''}`} hoverEffect={true}>
                  <div className="pricing-card-header">
                    <div className={`card-badge ${isHighlighted ? 'highlight-badge' : ''}`}>{t('pricing.eternal')}</div>
                    <h3 className="card-title">{moduleData.name}</h3>
                    <div className="card-price">{price}</div>
                  </div>
                  <ul className="card-features">
                    {features.map((feature, i) => (
                      <li key={i}>
                        <span className="check-icon"><i className="fas fa-check"></i></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="card-actions">
                    <button className="btn-buy-crypto" onClick={() => handleActionClick(moduleData.name, 'Crypto')}>
                      <i className="fab fa-bitcoin mr-2"></i>{t('pricing.buyCrypto')}
                    </button>
                    <button className="btn-ask" onClick={() => handleActionClick(moduleData.name, 'Question')}>
                      <i className="fas fa-question-circle mr-2"></i>{t('pricing.askQuestion')}
                    </button>
                  </div>
                </GlassCard>
              );
            })}
          </motion.div>
        );

      
      case 'partners':
        return (
          <motion.div key="partners" {...animationProps} className="pricing-grid">
            <GlassCard className="pricing-card" hoverEffect={true}>
              <div className="pricing-card-header">
                <div className="card-badge">{t('pricing.from')} 14 $</div>
                <h3 className="card-title">{t('pricing.partners.vps.title')}</h3>
              </div>
              <p className="card-desc">{t('pricing.partners.vps.desc')}</p>
              <div className="card-actions mt-auto">
                <button className="btn-secondary w-full" onClick={() => handleActionClick('VPS', 'Bot')}>
                  <i className="fab fa-telegram mr-2"></i>Telegram Bot
                </button>
                <button className="btn-ask w-full" onClick={() => handleActionClick('VPS', 'Question')}>
                  <i className="fas fa-question-circle mr-2"></i>{t('pricing.askQuestion')}
                </button>
              </div>
            </GlassCard>

            <GlassCard className="pricing-card" hoverEffect={true}>
              <div className="pricing-card-header">
                <div className="card-badge">{t('pricing.from')} 15 $</div>
                <h3 className="card-title">{t('pricing.partners.redirect.title')}</h3>
              </div>
              <p className="card-desc">{t('pricing.partners.redirect.desc')}</p>
              <div className="card-actions mt-auto">
                <button className="btn-secondary w-full" onClick={() => handleActionClick('Redirect', 'Site')}>
                  <i className="fas fa-globe mr-2"></i>Website
                </button>
                <button className="btn-ask w-full" onClick={() => handleActionClick('Redirect', 'Question')}>
                  <i className="fas fa-question-circle mr-2"></i>{t('pricing.askQuestion')}
                </button>
              </div>
            </GlassCard>

            <GlassCard className="pricing-card" hoverEffect={true}>
              <div className="pricing-card-header">
                <div className="card-badge">{t('pricing.from')} 7.5 $</div>
                <h3 className="card-title">{t('pricing.partners.proxies.title')}</h3>
              </div>
              <p className="card-desc">{t('pricing.partners.proxies.desc')}</p>
              <div className="card-actions mt-auto">
                <button className="btn-secondary w-full" onClick={() => handleActionClick('Proxies', 'Test')}>
                  <i className="fas fa-vial mr-2"></i>Get Test
                </button>
                <button className="btn-ask w-full" onClick={() => handleActionClick('Proxies', 'Question')}>
                  <i className="fas fa-question-circle mr-2"></i>{t('pricing.askQuestion')}
                </button>
              </div>
            </GlassCard>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <section className="pricing-section" id="pricing">
      <div className="section-header">
        <h2 className="section-title">{t('pricing.title')}</h2>
        <p className="section-subtitle">{t('pricing.subtitle')}</p>
        <span className="section-line" />
      </div>

      <div className="pricing-tabs-wrapper">
        <div className="pricing-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`pricing-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pricing-container">
        <AnimatePresence mode="wait">
          {renderContent()}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default PricingTable;
